import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Star } from "lucide-react";

export default function OverviewInsights({
  reputationScore = 0,
  sentimentData = [],
}) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-6">
      <ReputationScore score={reputationScore} />

      <ChartCard
        title="Sentiment Split"
        subtitle="Positive, neutral, and negative review mix"
      >
        {sentimentData.length > 0 ? (
          <div>
            <div className="h-[220px] sm:h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 12, right: 12, bottom: 12, left: 12 }}>
                  <Pie
                    data={sentimentData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={4}
                  >
                    {sentimentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>

                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 space-y-3">
              {sentimentData.map((item) => {
                const total = sentimentData.reduce(
                  (sum, entry) => sum + entry.value,
                  0,
                );

                const percent =
                  total > 0 ? Math.round((item.value / total) * 100) : 0;

                return (
                  <div key={item.name}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: item.fill }}
                        />

                        <span className="font-medium text-slate-700">
                          {item.name}
                        </span>
                      </div>

                      <span className="text-slate-500">
                        {percent}% · {item.value}
                      </span>
                    </div>

                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${percent}%`,
                          backgroundColor: item.fill,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-100 p-3">
              <p className="text-xs leading-5 text-slate-500">
                Positive sentiment is based on 4–5 star reviews. Neutral
                reflects 3-star feedback, while negative includes 1–2 star
                reviews that may need closer attention.
              </p>
            </div>
          </div>
        ) : (
          <div className="h-[260px] flex items-center justify-center text-sm text-slate-500">
            Gathering sentiment data...
          </div>
        )}
      </ChartCard>
    </div>
  );
}

function ReputationScore({ score }) {
  const helpingFactors = [
    "92% response rate",
    "Mostly positive customer sentiment",
    "Consistent review engagement",
  ];

  const hurtingFactors = [
    "3 unanswered reviews",
    "2 low-rating reviews this week",
    "Slower weekend response times",
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900">
            Reputation Score
          </h2>

          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Estimated reputation health based on review and response activity.
          </p>
        </div>

        <div className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <Star size={19} />
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-end gap-2">
          <p className="text-5xl font-bold text-slate-900">{score}</p>

          <div className="pb-1">
            <p className="text-sm text-slate-500">/100</p>

            <p className="text-xs font-medium text-green-600 mt-1">
              +6 this month
            </p>
          </div>
        </div>

        <div className="mt-4 h-3 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-indigo-600 rounded-full transition-all"
            style={{ width: `${score}%` }}
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5">
          {/* Helping */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-2 w-2 rounded-full bg-green-500" />

              <h3 className="text-sm font-semibold text-slate-900">
                What’s helping
              </h3>
            </div>

            <div className="space-y-2">
              {helpingFactors.map((factor) => (
                <div
                  key={factor}
                  className="flex items-start gap-2 text-sm text-slate-600"
                >
                  <span className="text-green-600 mt-0.5">↑</span>
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hurting */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-2 w-2 rounded-full bg-amber-500" />

              <h3 className="text-sm font-semibold text-slate-900">
                What’s hurting
              </h3>
            </div>

            <div className="space-y-2">
              {hurtingFactors.map((factor) => (
                <div
                  key={factor}
                  className="flex items-start gap-2 text-sm text-slate-600"
                >
                  <span className="text-amber-600 mt-0.5">↓</span>
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-6 border-t border-slate-100 pt-4">
          Reputation insights improve as more review and response activity is
          collected.
        </p>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-5 overflow-hidden">
      <div className="mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900">
          {title}
        </h2>

        <p className="text-xs sm:text-sm text-slate-500 mt-1">{subtitle}</p>
      </div>

      {children}
    </div>
  );
}
